/* eslint-disable no-unused-vars */
import {create} from 'zustand';

import {getFacetOptionLabelByKey} from './searchHelpers';

/**
 * Zustand TypeScript:
 * https://github.com/pmndrs/zustand/blob/main/docs/guides/typescript.md
 */
interface SearchState {
    userSearchTerm: string;
    funnelbackQueryParameters: FunnelbackQueryParameters;
    resultsSummary: ResultsSummary;
    searchFacetsJson: SearchFacet[];
    searchFacetSelections: SearchFacetSelections;
    searchSuggestions: SearchSuggestion[];
    recentSearches: RecentSearch[];
    queueScrollToForm: boolean;
    spellingSuggestion: string;
    searchTermHighlighting: boolean;
    setDefaultSortOrder: SetDefaultSortOrder;
    setDefaultResultsPerPage: SetDefaultResultsPerPage;
    setUserSearchTerm: SetUserSearchTerm;
    setFunnelbackQueryParameters: SetFunnelbackQueryParameters;
    setResultsSummary: SetResultsSummary;
    getSearchFacetDataByKey: GetSearchFacetDataByKey;
    setSearchFacetsJson: SetSearchFacetsJson;
    setSearchSuggestions: SetSearchSuggestions;
    setRecentSearches: SetRecentSearches;
    setQueueScrollToForm: SetQueueScrollToForm;
    getSearchFacetSelectionsByKey: GetSearchFacetSelectionsByKey;
    setSearchFacetSelectionsByKey: SetSearchFacetSelectionsByKey;
    setDefaultFacetSelection: SetDefaultFacetSelection;
    setBatchSearchFacetSelectionsByKey: SetBatchSearchFacetSelectionsByKey;
    resetFunnelbackQueryParameters: ResetFunnelbackQueryParameters;
    resetSearchFacetSelections: ResetSearchFacetSelections;
    resetRecentSearches: ResetRecentSearches;
    setSpellingSuggestion: SetSpellingSuggestion;
    setSearchTermHighlighting: SetSearchTermHighlighting;
}

interface ResultsSummary {
    fullyMatching: number;
    partiallyMatching: number;
    totalMatching: number;
    currentSetStart: number;
    currentSetEnd: number;
    prevStart: number | null;
    nextStart: number | null;
}

interface RecentSearch {
    query: string;
    localDateTimeSinceUnixEpoch: number;
}

interface SetDefaultSortOrder {
    (sortOrder: string): void;
}

interface SetDefaultResultsPerPage {
    (numRanks: number): void;
}

interface SetUserSearchTerm {
    (searchQuery: string): void;
}

interface SetFunnelbackQueryParameters {
    (queryPairs: FunnelbackQueryParameters, removeBlacklistedKeys?: boolean): void;
}

interface SetResultsSummary {
    (resultsSummaryJson: ResultsSummary): void;
}

interface GetSearchFacetDataByKey {
    (facetName: string): SearchFacetValue[];
}

interface SetSearchFacetsJson {
    (facetJson: SearchFacet[]): void;
}

interface SetSearchSuggestions {
    (suggestionsJson: SearchSuggestion[]): void;
}

interface SetRecentSearches {
    (searchQuery: string): void;
}

interface SetQueueScrollToForm {
    (queueScroll: boolean): void;
}

interface GetSearchFacetSelectionsByKey {
    (facetKey: string): string[];
}

interface SetSearchFacetSelectionsByKey {
    (facetKey: string, facetValues: string[]): void;
}

interface SetBatchSearchFacetSelectionsByKey {
    (facetKeyValuePairs: FacetQueryParameter[]): void;
}

interface SetDefaultFacetSelection {
    (facetSelections: SearchFacetSelections): void;
}

interface ResetFunnelbackQueryParameters {
    (): void;
}

interface ResetSearchFacetSelections {
    (): void;
}

interface ResetRecentSearches {
    (): void;
}

interface SetSpellingSuggestion {
    (string): void;
}

interface SetSearchTermHighlighting {
  (boolean): void;
}

/**
 * The search term to use on page load/reset.
 * i.e. Funnelback "query"
 */
const DEFAULT_SEARCH_TERM = '';

/**
 * The search results sorting order to use on page load/reset.
 * i.e. Funnelback "sort"
 */
let DEFAULT_SORT_ORDER = '';

/**
 * The default number of results to display per page.
 * i.e. Funnelback "num_ranks"
 */
let RESULTS_PER_PAGE = 10;

/**
 * Which page of results to show on page load/reset.
 * i.e. Funnelback "start_rank"
 */
const DEFAULT_RESULTS_PAGE = '1';

/**
 * The total number of "recent searches" to display on
 * the search page and store in the browser localStorage.
 */
const MAXIMUM_RECENT_SEARCHES = 4;

/**
 * The name/ID of the localStorage Object that is used
 * for the recent searches data.
 */
const LOCAL_STORAGE_NAME = 'SQUIZ_RECENT_SEARCHES';

/**
 * The spelling suggestion to use on page load/reset.
 */
const DEFAULT_SPELLING_SUGGESTION = '';

/**
 * A list of query keys to be removed from every search.
 * This can be used to clean-up the search parameters;
 * e.g. if someone has been linked to the search page from
 * somewhere else.
 * Or; to block (not in a secure way) specific user queries.
 */
const QUERY_BLACKLIST = ["fmo", "meta_adId"];

/**
 * The default facet selection to use on page load/reset.
 */
let DEFAULT_FACET_SELECTION: SearchFacetSelections = {};

/**
 * Get any Recent Searches that are stored in the browser localStorage Object.
 */
function getRecentSearchesFromLocalStorage(): RecentSearch[] {
    const localStorageValues = localStorage.getItem(LOCAL_STORAGE_NAME);
    let storedRecentSearches: RecentSearch[] = [];

    if (localStorageValues !== null) {
        try {
            storedRecentSearches = JSON.parse(localStorageValues);
        } catch (error) {
            // Replace nothing. We can't use this localStorage data.
            storedRecentSearches = [];
        }
    }

    /**
     * Confirm that the localStorage data is (roughly)
     * what we expect.
     * This is all extra error checking to ensure no one
     * has messed with (or broken) the localStorage data.
     */
    if (Array.isArray(storedRecentSearches)) {
        // We expect it will be an Array

        // Check the Array items
        for (let i = 0, j = storedRecentSearches.length; i < j; ++i) {
            if (storedRecentSearches[i]['query'] === undefined || storedRecentSearches[i]['localDateTimeSinceUnixEpoch'] === undefined) {
                // Missing some data.
                // Reset the localStorage values
                storedRecentSearches = [];
                break;
            }
        }
    } else {
        // Reset the localStorage values
        storedRecentSearches = [];
    }

    return storedRecentSearches;
}

const useSearchStore = create<SearchState>()((set, get) => ({
    userSearchTerm: DEFAULT_SEARCH_TERM, // This is the term that appears in the search input
    funnelbackQueryParameters: {
        query: DEFAULT_SEARCH_TERM,
        start_rank: DEFAULT_RESULTS_PAGE,
        sort: DEFAULT_SORT_ORDER,
        num_ranks: RESULTS_PER_PAGE,
    },
    resultsSummary: {
        fullyMatching: 0,
        partiallyMatching: 0,
        totalMatching: 0,
        currentSetStart: 0,
        currentSetEnd: 0,
        prevStart: null,
        nextStart: null,
    },
    searchFacetsJson: [],
    searchFacetSelections: DEFAULT_FACET_SELECTION,
    searchSuggestions: [],
    recentSearches: getRecentSearchesFromLocalStorage(),
    queueScrollToForm: false, // Used to trigger a scroll to the top of the form as results are loaded
    spellingSuggestion: DEFAULT_SPELLING_SUGGESTION,
    searchTermHighlighting: false, // Used to enable highlighting of current search term in result content
    setDefaultSortOrder: (sortOrder) => {
        DEFAULT_SORT_ORDER = sortOrder;

        /**
         * Replace/set the values for the num_ranks
         */
        const updatedQueryParams = {
            ...get().funnelbackQueryParameters,
            sort: DEFAULT_SORT_ORDER,
        };

        set((state) => ({funnelbackQueryParameters: updatedQueryParams}));
    },
    setDefaultResultsPerPage: (numRanks) => {
        RESULTS_PER_PAGE = numRanks;

        /**
         * Replace/set the values for the num_ranks
         */
        const updatedQueryParams = {
            ...get().funnelbackQueryParameters,
            num_ranks: RESULTS_PER_PAGE,
        };

        set((state) => ({funnelbackQueryParameters: updatedQueryParams}));
    },
    setUserSearchTerm: (searchQuery) => {
        /**
         * Replace the current search term
         */
        set((state) => ({userSearchTerm: searchQuery}));
    },
    setFunnelbackQueryParameters: (queryPairs, removeBlacklistedKeys = true) => {
        /**
         * Replace/set the values for the query keys
         */
        const updatedQueryParams = {
            ...get().funnelbackQueryParameters,
            ...queryPairs,
        };

        /**
         * Reset the pagination if a new query is provided, the number
         * of results per page has been changed or if a new sorting
         * option is applied.
         */
        if (queryPairs['query'] !== undefined || queryPairs['num_ranks'] !== undefined || queryPairs['sort'] !== undefined) {
            updatedQueryParams['start_rank'] = DEFAULT_RESULTS_PAGE;
        }

        /**
         * (re)set the result sorting to be by relevance (blank/default)
         * or by the default sort order if a new search query has been provided.
         */
        if (queryPairs['query'] !== undefined) {
            if (DEFAULT_SORT_ORDER !== '') {
                updatedQueryParams['sort'] = DEFAULT_SORT_ORDER;
            } else {
                updatedQueryParams['sort'] = '';
            }
        }

        /**
         * Remove any "blacklisted" items from the search query.
         */
        if (removeBlacklistedKeys) {
          for (let i = 0, j = QUERY_BLACKLIST.length; i < j; ++i) {
            if (updatedQueryParams[QUERY_BLACKLIST[i]] !== undefined) {
              delete updatedQueryParams[QUERY_BLACKLIST[i]];
            }
          }
        }

        set((state) => ({funnelbackQueryParameters: updatedQueryParams}));
    },
    setResultsSummary: (resultsSummaryJson) => {
        /**
         * Replace the search summary data
         */
        set((state) => ({resultsSummary: resultsSummaryJson}));
    },
    getSearchFacetDataByKey: (facetKey) => {
        /**
         * Retrieve the Funnelback JSON data for a specific Facet
         *
         * @param {String} facetKey The name of the Facet. e.g. "Resource Type"
         *
         * @returns Array
         */
        const allFacetData: SearchFacet[] = [...get().searchFacetsJson];
        const facetData: SearchFacetValue[] = [];
        let facetValues: SearchFacetValue[] = [];

        for (let i = 0, j = allFacetData.length; i < j; ++i) {
            const customOrder = allFacetData[i]?.customOrder ?? [];

            // If a customOrder has been set, override the facet data being returned
            if (customOrder.length && allFacetData[i].name === facetKey) {
                // Find the matching facet data
                facetValues = allFacetData[i].allValues;

                // Check if any facet options are available for the provided key
                if (facetValues.length !== 0) {
                    // Filter for the facetValues that match the specified labels in customOrder
                    const filteredFacetValues = facetValues.filter((facetValue) => customOrder.indexOf(facetValue.label) > -1);

                    // Sort the filtered values to match the custom order
                    const sortedFacetValues = filteredFacetValues.sort((facetValueA, facetValueB) => {
                        if (customOrder.indexOf(facetValueA.label) < customOrder.indexOf(facetValueB.label)) return -1;

                        if (customOrder.indexOf(facetValueA.label) > customOrder.indexOf(facetValueB.label)) return 1;

                        return 0;
                    });

                    // Push the sorted values
                    facetData.push(...sortedFacetValues);
                }
            } else if (allFacetData[i].name === facetKey) {
                // Find the matching facet data
                facetValues = allFacetData[i].allValues;

                // Check if any facet options are available for the provided key
                if (facetValues.length !== 0) {
                    // Parse the options into the required format
                    for (let x = 0, b = facetValues.length; x < b; ++x) {
                        // ! Hardcoded Overriding Duplicates, only push the "G+T Innovate" labelled facet that's using the correct spelling
                        // ! Bug Tracking Page: https://squizgroup.atlassian.net/wiki/spaces/GTW/pages/edit-v2/3499982928
                        // ! DXP Support Ticket: DXPSUP-1142
                        // ! Product Bug Ticket: Pending...
                        if (!/G T Innovate/gi.test(facetValues[x].label)) {
                            facetData.push({
                                count: facetValues[x].count,
                                label: getFacetOptionLabelByKey(facetValues[x].label),
                                value: facetValues[x].value,
                            });
                        }
                    }
                }
            }
        }

        return facetData;

        /**
         * Retrieve the Funnelback JSON data for a specific facet
         *
         * @param facetKey The parameter name of the Facet. e.g. "f.Facet name|FacetName"
         */
        // const allFacetData: SearchFacet[] = [...get().searchFacetsJson];
        // let facetData: SearchFacetValue[] = [];

        // // Check if any facet options are available for the provided key
        // for (let i = 0, j = allFacetData.length; i < j; ++i) {
        //     if (allFacetData[i].queryStringParamName === facetQueryName) {
        //         // Capture all the values for this facet
        //         facetData = allFacetData[i].allValues;
        //         break;
        //     }
        // }

        // return facetData;
    },
    setSearchFacetsJson: (facetJson) => {
        /**
         * Replace the search facets JSON data in the global state.
         */
        set((state) => ({searchFacetsJson: [...facetJson]}));
    },
    setSearchSuggestions: (suggestionsJson) => {
        /**
         * Replace the current search suggestions
         */
        set((state) => ({searchSuggestions: suggestionsJson}));
    },
    setRecentSearches: (searchQuery) => {
        // Create a new RecentSearch Object
        const recentSearch: RecentSearch = {
            query: '',
            localDateTimeSinceUnixEpoch: 0,
        };
        // Retrieve any stored searches
        let updatedRecentSearches: RecentSearch[] = getRecentSearchesFromLocalStorage();

        /**
         * Check that the query isn't blank.
         */
        if (searchQuery !== '') {
            /**
             * Check that the query isn't a repeat
             * of the previous one.
             */
            if (updatedRecentSearches.length !== 0 && searchQuery === updatedRecentSearches[0]['query']) {
                // Do nothing here. We just want to skip updating the state with this value.
            } else {
                // Capture the new query
                recentSearch['query'] = searchQuery;

                // Add the timestamp
                recentSearch['localDateTimeSinceUnixEpoch'] = Date.now();

                // Add the new item as the first item in the recent searches
                updatedRecentSearches.splice(0, 0, recentSearch);

                // If recent searches has exceeded the maximum, drop the extra items
                if (updatedRecentSearches.length > MAXIMUM_RECENT_SEARCHES) {
                    updatedRecentSearches.splice(MAXIMUM_RECENT_SEARCHES);
                }

                /**
                 * Update the recent searches state.
                 */
                set((state) => ({recentSearches: updatedRecentSearches}));

                /**
                 * Replace the recent searches saved in the localStorage.
                 */
                localStorage.setItem(LOCAL_STORAGE_NAME, JSON.stringify(updatedRecentSearches));
            }
        }
    },
    setQueueScrollToForm: (queueScroll) => {
        /**
         * Replace the current value
         */
        set((state) => ({queueScrollToForm: queueScroll}));
    },
    getSearchFacetSelectionsByKey: (facetParamName) => {
        /**
         * Retrieve the selected options for a specific Facet
         *
         * @param facetParamName The parameter name of the Facet. e.g. "f.Facet name|FacetName"
         */
        const allFacetSelections = {
            ...get().searchFacetSelections,
        };
        let facetSelections: string[] = [];

        if (allFacetSelections[facetParamName] !== undefined) {
            facetSelections = allFacetSelections[facetParamName];
        }

        return facetSelections;
    },
    setSearchFacetSelectionsByKey: (facetParamName, facetValues) => {
        /**
         * Replace/set the selected options for a specific Facet
         */
        const updatedFacetData = {
            ...get().searchFacetSelections,
        };

        /**
         * Capture the current query parameters
         */
        const updatedQueryParams = {
            ...get().funnelbackQueryParameters,
        };

        if (facetValues.length === 0) {
            // If there are no values selected, remove the key entirely
            delete updatedFacetData[facetParamName];
        } else {
            // Just replace the existing key in state
            updatedFacetData[facetParamName] = facetValues;
        }

        // Reset the pagination after a facet has been updated
        updatedQueryParams['start_rank'] = DEFAULT_RESULTS_PAGE;

        /**
         * Remove any "blacklisted" items from the search query.
         */
        for (let i = 0, j = QUERY_BLACKLIST.length; i < j; ++i) {
          if (updatedQueryParams[QUERY_BLACKLIST[i]] !== undefined) {
            delete updatedQueryParams[QUERY_BLACKLIST[i]];
          }
        }

        set((state) => ({
            funnelbackQueryParameters: updatedQueryParams,
            searchFacetSelections: updatedFacetData,
        }));
    },
    setBatchSearchFacetSelectionsByKey: (facetKeyValuePairs) => {
        /**
         * Replace/set multiple Facets at the same time.
         */
        const updatedFacetData = {
            ...get().searchFacetSelections,
        };

        /**
         * Capture the current query parameters
         */
        const updatedQueryParams = {
            ...get().funnelbackQueryParameters,
        };

        for (let i = 0, j = facetKeyValuePairs.length; i < j; ++i) {
            updatedFacetData[facetKeyValuePairs[i].queryStringParamName] = facetKeyValuePairs[i].queryStringParamValue;
        }

        // Reset the pagination after a facet has been updated
        updatedQueryParams['start_rank'] = DEFAULT_RESULTS_PAGE;

        /**
         * Remove any "blacklisted" items from the search query.
         */
        for (let i = 0, j = QUERY_BLACKLIST.length; i < j; ++i) {
          if (updatedQueryParams[QUERY_BLACKLIST[i]] !== undefined) {
            delete updatedQueryParams[QUERY_BLACKLIST[i]];
          }
        }

        set((state) => ({
            funnelbackQueryParameters: updatedQueryParams,
            searchFacetSelections: updatedFacetData,
        }));
    },
    setDefaultFacetSelection: (facetSelections) => {
        DEFAULT_FACET_SELECTION = facetSelections;

        /**
         * Replace/set multiple Facets at the same time.
         */
        const updatedFacetData = {
            ...get().searchFacetSelections,
        };

        /**
         * Capture the current query parameters
         */
        const updatedQueryParams = {
            ...get().funnelbackQueryParameters,
        };

        // Reset the pagination after a facet has been updated
        updatedQueryParams['start_rank'] = DEFAULT_RESULTS_PAGE;

        /**
         * Remove any "blacklisted" items from the search query.
         */
        for (let i = 0, j = QUERY_BLACKLIST.length; i < j; ++i) {
          if (updatedQueryParams[QUERY_BLACKLIST[i]] !== undefined) {
            delete updatedQueryParams[QUERY_BLACKLIST[i]];
          }
        }

        set((state) => ({
            funnelbackQueryParameters: updatedQueryParams,
            searchFacetSelections: {...DEFAULT_FACET_SELECTION, ...updatedFacetData},
        }));
    },
    resetFunnelbackQueryParameters: () => {
        /**
         * Revert the query values to their defaults
         */
        set((state) => ({
            funnelbackQueryParameters: {
                query: DEFAULT_SEARCH_TERM,
                start_rank: DEFAULT_RESULTS_PAGE,
                sort: DEFAULT_SORT_ORDER,
                num_ranks: RESULTS_PER_PAGE,
            },
            searchFacetSelections: DEFAULT_FACET_SELECTION,
        }));
    },
    resetSearchFacetSelections: () => {
        /**
         * Clear the search selections data
         */

        /**
         * Capture the current query parameters
         */
        const updatedQueryParams = {
            ...get().funnelbackQueryParameters,
        };

        // Reset the pagination after a facet has been updated
        updatedQueryParams['start_rank'] = DEFAULT_RESULTS_PAGE;

        /**
         * Remove any "blacklisted" items from the search query.
         */
        for (let i = 0, j = QUERY_BLACKLIST.length; i < j; ++i) {
          if (updatedQueryParams[QUERY_BLACKLIST[i]] !== undefined) {
            delete updatedQueryParams[QUERY_BLACKLIST[i]];
          }
        }

        set((state) => ({
            funnelbackQueryParameters: updatedQueryParams,
            searchFacetSelections: DEFAULT_FACET_SELECTION,
        }));
    },
    resetRecentSearches: () => {
        /**
         * Remove all the recent searches data
         */
        localStorage.removeItem(LOCAL_STORAGE_NAME);
        set((state) => ({recentSearches: []}));
    },
    setSpellingSuggestion: (suggestion) => {
        /**
         * Set the spelling suggestion, which will update any
         * html rendering if it's being used
         */
        set((state) => ({
            spellingSuggestion: suggestion,
        }));
    },
    setSearchTermHighlighting: (isEnabled) => {
      /**
       * Replace the current state
       */
      set((state) => ({searchTermHighlighting: isEnabled}));
    }
}));

export default useSearchStore;
